import { useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import circlePlaceholder from '../assets/circle-placeholder.svg'
import { z } from 'zod'

export const PlatformFeaturesSchema = z.object({
  has_dataplayr: z.boolean(),
  has_learning_module: z.boolean(),
  has_clair_ai: z.boolean(),
  has_arena: z.boolean(),
})

export const ActiveCompetitionsResponseSchema = z.object({
  competitions: z.array(z.lazy(() => CompetitionSchema)),
  platform_features: PlatformFeaturesSchema,
})

const getActiveCompetitions = async () => {
  const response = await axios.get('/api/competitions/active')
  return ActiveCompetitionsResponseSchema.parse(response.data)
}

export const useActiveCompetitions = () => {
  return useSuspenseQuery({
    queryKey: ['activeCompetitions'],
    queryFn: () => getActiveCompetitions(),
  })
}

async function getCompetitionDetails({
  competition_id,
}: {
  competition_id: number
}) {
  const response = await axios.get(`/api/competitions/${competition_id}`)
  return CompetitionSchema.parse(response.data)
}

export function useCompetitionDetails({
  competition_id,
}: {
  competition_id: number
}) {
  return useSuspenseQuery({
    queryKey: ['competitionDetails', { competition_id }],
    queryFn: () => {
      if (competition_id === 0) return null
      return getCompetitionDetails({ competition_id })
    },
  })
}

export const CompetitionLogoSchema = z.object({
  main: z
    .string()
    .url()
    .optional()
    .default(circlePlaceholder)
    .catch(circlePlaceholder),
  mini: z
    .string()
    .url()
    .optional()
    .default(circlePlaceholder)
    .catch(circlePlaceholder),
  thumbnail: z
    .string()
    .url()
    .optional()
    .default(circlePlaceholder)
    .catch(circlePlaceholder),
})

export const CompetitionSchema = z.object({
  competition_id: z.number(),
  name: z.string(),
  logo: CompetitionLogoSchema,
  starts_at: z.string().datetime(),
  ends_at: z.string().datetime(),
  kind: z.enum(['race', 'match']),
  single_player_teams: z.boolean(),
})

export const CompetitionSelectItemSchema = z.object({
  value: z.string(),
  label: z.string(),
  logo: z.string().url(),
  logo_selected: z.string().url(),
})

export type Competition = z.infer<typeof CompetitionSchema>
export type CompetitionSelectItem = z.infer<typeof CompetitionSelectItemSchema>
export type PlatformFeatures = z.infer<typeof PlatformFeaturesSchema>
export type ActiveCompetitionsResponse = z.infer<
  typeof ActiveCompetitionsResponseSchema
>
